import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Default pinned apps (shown in main sidebar)
export const DEFAULT_PINNED_APPS = ['ai', 'help'];

// All available customizable apps (excludes home, workspace, docs which are always visible)
export const ALL_CUSTOMIZABLE_APPS = [
  { id: 'ai', label: 'AI', isInternal: true },
  { id: 'help', label: 'Help', isInternal: true },
  { id: 'getting-started', label: 'Getting Started', url: 'https://help.queueos.com/collection/getting-started', isInternal: false },
  { id: 'feedback', label: 'Feedback', url: 'https://queueos.com/forms/feedback-on-queueos', isInternal: false },
  { id: 'spotify', label: 'Spotify', isInternal: true },
];

interface UserAppPreferences {
  app_order: string[];
  pinned_apps: string[];
  beta_dashboard_enabled?: boolean;
}

export function useUserAppPreferences() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserAppPreferences>({ 
    app_order: [], 
    pinned_apps: DEFAULT_PINNED_APPS 
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    loadPreferences();
  }, [user]);

  const loadPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_app_preferences')
        .select('app_order, preferences')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        const prefs = data.preferences as { beta_dashboard_enabled?: boolean; pinned_apps?: string[] } | null;
        setPreferences({ 
          app_order: data.app_order as string[],
          pinned_apps: prefs?.pinned_apps ?? DEFAULT_PINNED_APPS,
          beta_dashboard_enabled: prefs?.beta_dashboard_enabled ?? false
        });
      }
    } catch (error) {
      console.error('Error loading app preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveAppOrder = async (appOrder: string[]) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_app_preferences')
        .upsert(
          {
            user_id: user.id,
            app_order: appOrder,
          },
          {
            onConflict: 'user_id'
          }
        );

      if (error) throw error;

      setPreferences(prev => ({ ...prev, app_order: appOrder }));
    } catch (error) {
      console.error('Error saving app preferences:', error);
      toast.error('Failed to save app order preferences');
    }
  };

  const savePinnedApps = async (pinnedApps: string[]) => {
    if (!user) return;

    try {
      // Get current preferences to preserve other fields
      const { data: currentData } = await supabase
        .from('user_app_preferences')
        .select('preferences, app_order')
        .eq('user_id', user.id)
        .single();

      const currentPrefs = (currentData?.preferences as { beta_dashboard_enabled?: boolean; pinned_apps?: string[] } | null) || {};

      const { error } = await supabase
        .from('user_app_preferences')
        .upsert(
          {
            user_id: user.id,
            app_order: currentData?.app_order || preferences.app_order,
            preferences: { ...currentPrefs, pinned_apps: pinnedApps },
          },
          {
            onConflict: 'user_id'
          }
        );

      if (error) throw error;

      setPreferences(prev => ({ ...prev, pinned_apps: pinnedApps }));
    } catch (error) {
      console.error('Error saving pinned apps:', error);
      toast.error('Failed to save sidebar preferences');
    }
  };

  const togglePinnedApp = async (appId: string) => {
    const currentPinned = preferences.pinned_apps || DEFAULT_PINNED_APPS;
    const newPinned = currentPinned.includes(appId)
      ? currentPinned.filter(id => id !== appId)
      : [...currentPinned, appId];
    
    await savePinnedApps(newPinned);
  };

  const saveBetaDashboard = async (enabled: boolean) => {
    if (!user) return;

    try {
      // Get current preferences to preserve other fields
      const { data: currentData } = await supabase
        .from('user_app_preferences')
        .select('preferences')
        .eq('user_id', user.id)
        .single();

      const currentPrefs = (currentData?.preferences as { beta_dashboard_enabled?: boolean; pinned_apps?: string[] } | null) || {};

      const { error } = await supabase
        .from('user_app_preferences')
        .upsert(
          {
            user_id: user.id,
            app_order: preferences.app_order,
            preferences: { ...currentPrefs, beta_dashboard_enabled: enabled },
          },
          {
            onConflict: 'user_id'
          }
        );

      if (error) throw error;

      setPreferences(prev => ({ ...prev, beta_dashboard_enabled: enabled }));
      toast.success(enabled ? 'Beta Dashboard enabled' : 'Beta Dashboard disabled');
    } catch (error) {
      console.error('Error saving beta dashboard preference:', error);
      toast.error('Failed to save preference');
    }
  };

  return {
    preferences,
    saveAppOrder,
    savePinnedApps,
    togglePinnedApp,
    saveBetaDashboard,
    isLoading,
  };
}